import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OneTimePaymentButton } from '@/components/payment';
import { toast } from 'sonner';

interface QuickChargeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  familyId: string;
  familyName?: string;
}

const QuickChargeDialog: React.FC<QuickChargeDialogProps> = ({ 
  isOpen, 
  onClose, 
  familyId,
  familyName
}) => {
  const [amount, setAmount] = useState<number>(0);
  const [description, setDescription] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [amountInput, setAmountInput] = useState<string>('$0.00');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('card');
  const [payerName, setPayerName] = useState<string>('');
  const [payerEmail, setPayerEmail] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  
  // Reset the form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setAmount(0);
      setAmountInput('$0.00');
      setDescription('');
      setCategory('');
      setPaymentMethod('card');
      setPayerName('');
      setPayerEmail('');
      setNotes('');
      setValidationError(null);
    }
  }, [isOpen]);
  
  const formatCurrencyInput = (value: string) => {
    // Remove all non-numeric characters except decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    // Handle the decimal point appropriately
    const parts = numericValue.split('.');
    const dollars = parts[0] || '0';
    let cents = '';
    
    if (parts.length > 1) {
      // Ensure we have at most 2 decimal places
      cents = parts[1].slice(0, 2);
      // Pad with zeros if needed
      if (cents.length === 1) cents += '0';
    } else {
      cents = '00';
    }
    
    // Format the value with dollar sign
    return `$${dollars}.${cents}`;
  };
  
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers and decimal points in the input
    const inputValue = e.target.value;
    
    // Format the input value
    const formattedValue = formatCurrencyInput(inputValue);
    setAmountInput(formattedValue);
    
    // Calculate the amount in cents
    const numericValue = formattedValue.replace(/[^0-9.]/g, '');
    const parsedAmount = parseFloat(numericValue);
    
    if (!isNaN(parsedAmount)) {
      // Convert dollars to cents (multiply by 100 and round to avoid floating point issues)
      setAmount(Math.round(parsedAmount * 100));
      setValidationError(null);
    } else {
      setAmount(0);
      setValidationError('Please enter a valid amount');
    }
    
    console.log('Amount in cents:', Math.round(parsedAmount * 100));
  };
  
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value);
    if (!e.target.value.trim()) {
      setValidationError('Description is required');
    } else {
      setValidationError(null);
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCategory(e.target.value);
  };
  
  const handlePayerNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPayerName(e.target.value);
  };
  
  const handlePayerEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPayerEmail(e.target.value);
  };
  
  const handleNotesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotes(e.target.value);
  };
  
  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPaymentMethod(e.target.value);
  };
  
  const validateForm = () => {
    if (amount <= 0) {
      setValidationError('Amount must be greater than zero');
      return false;
    }
    if (!description.trim()) {
      setValidationError('Description is required');
      return false;
    }
    if (!payerName.trim()) {
      setValidationError('Payer name is required');
      return false;
    }
    if (!payerEmail.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payerEmail)) {
      setValidationError('Valid payer email is required');
      return false;
    }
    setValidationError(null);
    return true;
  };
  
  const isValid = amount > 0 && description.trim().length > 0 && payerName.trim().length > 0 && payerEmail.trim().length > 0 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payerEmail);
  
  const handlePaymentSuccess = () => {
    toast.success('Payment processing initiated', {
      description: 'Redirecting to the payment page...'
    });
  };
  
  const handlePaymentError = (error: Error) => {
    toast.error('Payment failed', {
      description: error.message || 'Please try again later'
    });
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Quick Charge{familyName ? `: ${familyName}` : ''}</DialogTitle>
          <DialogDescription>
            Enter an amount and description for this charge.
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="amount" className="text-right">
              Amount
            </Label>
            <div className="col-span-3 relative">
              <Input
                id="amount"
                type="text"
                value={amountInput}
                onChange={handleAmountChange}
                className="pl-7"
                placeholder="$0.00"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="description" className="text-right">
              Description
            </Label>
            <Input
              id="description"
              className="col-span-3"
              value={description}
              onChange={handleDescriptionChange}
              placeholder="e.g., Private lesson, Costume fee"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="category" className="text-right">
              Category
            </Label>
            <select
              id="category"
              className="col-span-3 border rounded px-2 py-1"
              value={category}
              onChange={handleCategoryChange}
            >
              <option value="">Select a category (optional)</option>
              <option value="tuition">Tuition</option>
              <option value="lesson">Private Lesson</option>
              <option value="fee">Fee</option>
              <option value="costume">Costume</option>
              <option value="merchandise">Merchandise</option>
              <option value="competition">Competition</option>
              <option value="event">Event</option>
              <option value="camp">Camp</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="payerName" className="text-right">
              Payer Name
            </Label>
            <Input
              id="payerName"
              className="col-span-3"
              value={payerName}
              onChange={handlePayerNameChange}
              placeholder="Full name of payer"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="payerEmail" className="text-right">
              Payer Email
            </Label>
            <Input
              id="payerEmail"
              className="col-span-3"
              value={payerEmail}
              onChange={handlePayerEmailChange}
              placeholder="payer@email.com"
              type="email"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="paymentMethod" className="text-right">
              Payment Method
            </Label>
            <select
              id="paymentMethod"
              className="col-span-3 border rounded px-2 py-1"
              value={paymentMethod}
              onChange={handlePaymentMethodChange}
            >
              <option value="card">Card</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="other">Other</option>
            </select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="notes" className="text-right">
              Notes
            </Label>
            <Input
              id="notes"
              className="col-span-3"
              value={notes}
              onChange={handleNotesChange}
              placeholder="Internal notes (optional)"
            />
          </div>
          
          {validationError && (
            <div className="text-sm text-red-500 px-4">
              {validationError}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          
          {isValid ? (
            <OneTimePaymentButton
              amount={amount}
              name="Quick Charge Payment"
              description={description}
              metadata={{
                family_id: familyId,
                charge_type: 'quick_charge',
                category: category,
                description: description,
                payment_method: paymentMethod,
                payer_name: payerName,
                payer_email: payerEmail,
                notes: notes
              }}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
            >
              Process Payment
            </OneTimePaymentButton>
          ) : (
            <Button disabled>Process Payment</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickChargeDialog;
